import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';

import { LoginLayout } from '~/components/layouts/login-layout';
import { m } from '~/paraglide/messages.js';
import { AccountTypeSelection } from './-components/account-type-selection';
import { ActivateCardComponent } from './-components/activate-card';
import { BusinessProfileForm } from './-components/business-profile';
import { ConnectBloqueComponent } from './-components/connect-bloque';
import { FreelancerProfileForm } from './-components/freelancer-profile';
import { IndividualProfileForm } from './-components/individual-profile';
import { InviteTeamComponent } from './-components/invite-team';
import { OnboardingStepIndicator } from './-components/onboarding-step-indicator';
import { useOnboardingForm } from './-hooks/use-onboarding-form';
import { useOnboardingSteps } from './-hooks/use-onboarding-steps';

export const Route = createFileRoute('/_authenticated/onboarding/')({
  beforeLoad: async ({ context }) => {
    if (context.auth.user?.metadata?.onboarding) {
      throw redirect({
        to: '/',
        replace: true,
      });
    }
  },
  component: OnboardingPage,
});

type StepDef = {
  id: string;
  label: string;
  title: string;
  description: string;
};

function getSteps(isBusiness: boolean): StepDef[] {
  const base: StepDef[] = [
    {
      id: 'account_type',
      label: m['onboarding.steps.account_type'](),
      title: m['onboarding.step0.title'](),
      description: m['onboarding.step0.description'](),
    },
    {
      id: 'profile',
      label: m['onboarding.steps.profile'](),
      title: m['onboarding.step1.title'](),
      description: m['onboarding.step1.description'](),
    },
  ];

  if (isBusiness) {
    base.push({
      id: 'invite_team',
      label: m['onboarding.steps.invite_team'](),
      title: m['onboarding.invite_team.title'](),
      description: m['onboarding.invite_team.description'](),
    });
  }

  base.push(
    {
      id: 'connect_bloque',
      label: m['onboarding.steps.connect_bloque'](),
      title: m['onboarding.step_connect.title'](),
      description: m['onboarding.step_connect.description'](),
    },
    {
      id: 'activate_card',
      label: m['onboarding.steps.activate_card'](),
      title: m['onboarding.step_card.title'](),
      description: m['onboarding.step_card.description'](),
    },
  );

  return base;
}

function OnboardingPage() {
  const navigate = useNavigate();

  const {
    formData,
    setAccountType,
    updateBusinessProfile,
    updateFreelancerProfile,
    updateIndividualProfile,
    updateTeamMembers,
    completeOnboarding,
    isSubmitting,
  } = useOnboardingForm();

  const isBusiness = formData.accountType === 'business';
  const steps = useMemo(() => getSteps(isBusiness), [isBusiness]);
  const totalSteps = steps.length;

  const { currentStep, goToNextStep, goToPrevStep } =
    useOnboardingSteps(totalSteps);

  const currentStepDef = steps[currentStep];

  const handleAccountTypeSelect = (
    type: Parameters<typeof setAccountType>[0],
  ) => {
    setAccountType(type);
    goToNextStep();
  };

  const handleActivate = async () => {
    const success = await completeOnboarding();
    if (success) {
      navigate({ to: '/verify-identity/kyc' });
    }
  };

  const handleSkip = async () => {
    const success = await completeOnboarding();
    if (success) {
      navigate({ to: '/' });
    }
  };

  const renderStep = () => {
    const stepId = currentStepDef?.id;

    switch (stepId) {
      case 'account_type':
        return <AccountTypeSelection onSelect={handleAccountTypeSelect} />;

      case 'profile':
        if (formData.accountType === 'business') {
          return (
            <BusinessProfileForm
              values={formData.businessProfile}
              onSubmit={(data) => {
                updateBusinessProfile(data);
                goToNextStep();
              }}
            />
          );
        }
        if (formData.accountType === 'freelancer') {
          return (
            <FreelancerProfileForm
              values={formData.freelancerProfile}
              onSubmit={(data) => {
                updateFreelancerProfile(data);
                goToNextStep();
              }}
            />
          );
        }
        return (
          <IndividualProfileForm
            values={formData.individualProfile}
            onSubmit={(data) => {
              updateIndividualProfile(data);
              goToNextStep();
            }}
          />
        );

      case 'invite_team':
        return (
          <InviteTeamComponent
            values={formData.teamMembers}
            onSubmit={(members) => {
              updateTeamMembers(members);
              goToNextStep();
            }}
            onSkip={goToNextStep}
          />
        );

      case 'connect_bloque':
        return <ConnectBloqueComponent onNext={goToNextStep} />;

      case 'activate_card':
        return (
          <ActivateCardComponent
            onActivate={handleActivate}
            onSkip={handleSkip}
            isSubmitting={isSubmitting}
          />
        );

      default:
        return null;
    }
  };

  return (
    <LoginLayout>
      <OnboardingStepIndicator
        labels={steps.map((s) => s.label)}
        currentStep={currentStep}
      />

      <div className="flex min-h-[80vh] w-full flex-col items-center justify-center">
        <div className="mb-6 w-full max-w-md">
          <div className="mb-4 flex items-center">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={goToPrevStep}
                className="flex items-center gap-2 rounded px-2 py-1 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">{m['common.back']()}</span>
              </button>
            )}
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold text-foreground">
              {currentStepDef?.title}
            </h1>
            <p className="text-muted-foreground">
              {currentStepDef?.description}
            </p>
          </div>
        </div>

        <div className="flex w-full justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStepDef?.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="w-full flex justify-center"
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </LoginLayout>
  );
}
